// src/pages/CustomerReport/ManageCustomerLogins.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { customerNoOf } from '../../components/CustomerReportShell';
import CustomerVpaManagerDialog from '../../components/CustomerVpaManagerDialog';


// Helper to normalize district names (Trivandrum <-> Thiruvananthapuram)
const normalizeDistrictName = (name) => {
  if (!name || name === 'N/A') return '';
  const n = String(name).trim().toLowerCase();
  if (n.includes('trivandrum') || n.includes('thiruvananthapuram') || n.includes('trivandram')) return 'thiruvananthapuram';
  if (n.includes('cochin') || n.includes('ernakulam')) return 'ernakulam';
  if (n.includes('calicut') || n.includes('kozhikode')) return 'kozhikode';
  if (n.includes('trichur') || n.includes('thrissur')) return 'thrissur';
  if (n.includes('palghat') || n.includes('palakkad')) return 'palakkad';
  if (n.includes('cannanore') || n.includes('kannur')) return 'kannur';
  if (n.includes('alleppey') || n.includes('alappuzha')) return 'alappuzha';
  if (n.includes('quilon') || n.includes('kollam')) return 'kollam';
  return n;
};

const ManageCustomerLogins = () => {
  const { can } = usePermissions();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedState, setSelectedState] = useState('All');
  const [selectedDistrict, setSelectedDistrict] = useState('All');
  const [selectedTaluk, setSelectedTaluk] = useState('All');
  const [pincodeFilter, setPincodeFilter] = useState('');

  // Dropdown options
  const [districtList, setDistrictList] = useState([]);
  const [talukList, setTalukList] = useState([]);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editMobile, setEditMobile] = useState('');
  const [editPincode, setEditPincode] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  // VPA manager (add / delete VPAs of the customer)
  const [vpaCustomer, setVpaCustomer] = useState(null);
  const canManageVpa = can('customers.logins.addVpa') || can('customers.logins.deleteVpa')
    || can('customers.all.addVpa') || can('customers.all.deleteVpa') || can('customers.all.editVpa');

  // Status / Block Modal State
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState('active');
  const [statusReason, setStatusReason] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // Fetch Districts
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/districts`);
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : res.data.districts || [];
          setDistrictList(list);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Taluks when district changes
  useEffect(() => {
    const fetchTaluks = async () => {
      if (selectedDistrict === 'All') {
        setTalukList([]);
        setSelectedTaluk('All');
        return;
      }
      try {
        const distObj = districtList.find(d => (d.districtName || d.name) === selectedDistrict);
        const distId = distObj ? distObj._id : selectedDistrict;
        const res = await axios.get(`${API_BASE_URL}/taluks/district/${distId}`);
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : res.data.taluks || [];
          setTalukList(list);
        }
      } catch (err) {
        console.error('Error fetching taluks:', err);
        setTalukList([]);
      }
    };
    fetchTaluks();
  }, [selectedDistrict, districtList]);

  // Fetch Customers
  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/customerProfiles`, {
        params: {
          limit: 2000,
          level: 1
        }
      });
      if (res.status === 200) {
        const data = res.data.customers || res.data.data || [];
        setCustomers(data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to fetch customers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Apply Robust Frontend Filtering
  const filteredCustomers = customers.filter(c => {
    // District filter with normalization & fallback
    if (selectedDistrict !== 'All') {
      const targetNorm = normalizeDistrictName(selectedDistrict);
      const cNorm = normalizeDistrictName(c.customerDistrictName);
      let isDistMatch = false;

      if (cNorm && targetNorm && (cNorm === targetNorm || cNorm.includes(targetNorm) || targetNorm.includes(cNorm))) {
        isDistMatch = true;
      } else {
        const pin = Number(c.customerPincode);
        const taluk = (c.customerTalukName || '').toLowerCase();
        if (targetNorm === 'thiruvananthapuram') {
          if ((pin >= 695000 && pin <= 695999) || taluk.includes('thiruvananthapuram') || taluk.includes('trivandrum') || taluk.includes('neyyattinkara') || taluk.includes('nedumangad') || taluk.includes('attingal')) {
            isDistMatch = true;
          }
        }
      }

      if (!isDistMatch) return false;
    }

    // Village/Taluk filter
    if (selectedTaluk !== 'All') {
      const cTaluk = (c.customerTalukName || '').toLowerCase();
      if (!cTaluk.includes(selectedTaluk.toLowerCase())) return false;
    }

    // Pincode filter
    if (pincodeFilter.trim()) {
      const cPin = String(c.customerPincode || '');
      if (!cPin.includes(pincodeFilter.trim())) return false;
    }

    // Search by Name, Mobile, Customer ID (= Customer No), VPA
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const cName = (c.customerName || '').toLowerCase();
      const cMobile = (c.customerMobile || '').toLowerCase();
      const fullId = String(c.customerId || c._id || '').toLowerCase();
      const shortId = String(c.customerNo || '').toLowerCase();
      const vpaStr = Array.isArray(c.customerVPA)
        ? c.customerVPA.map(v => (typeof v === 'string' ? v : (v?.vpa || ''))).join(' ').toLowerCase()
        : '';
      if (!cName.includes(q) && !cMobile.includes(q) && !(fullId && fullId === q) && !shortId.includes(q) && !vpaStr.includes(q)) {
        return false;
      }
    }
    return true;
  });

  // Open Edit Modal
  const handleOpenEdit = (customer) => {
    if (!can('customers.logins.edit')) return;
    setSelectedCustomer(customer);
    setEditName(customer.customerName || '');
    setEditEmail(customer.customerEmail || '');
    setEditMobile(customer.customerMobile || '');
    setEditPincode(customer.customerPincode || '');
    setEditModalOpen(true);
  };

  // Submit Edit Form
  const handleSaveEdit = async () => {
    if (!can('customers.logins.edit')) return;
    if (!selectedCustomer) return;
    setEditSubmitting(true);
    try {
      const cId = selectedCustomer.customerId || selectedCustomer._id;
      const res = await axios.put(`${API_BASE_URL}/customerProfiles/${cId}`, {
        customerName: editName,
        customerEmail: editEmail,
        customerMobile: editMobile,
        customerPincode: Number(editPincode)
      });
      if (res.status === 200) {
        setSuccessMsg('Customer details updated successfully!');
        setEditModalOpen(false);
        fetchCustomers();
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      alert(err.response?.data?.message || 'Failed to update customer details.');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Open Status/Block Modal
  const handleOpenStatusModal = (customer, newStatus) => {
    if (!can('customers.logins.block')) return;
    setSelectedCustomer(customer);
    setTargetStatus(newStatus);
    setStatusReason('');
    setStatusModalOpen(true);
  };

  // Submit Status Change
  const handleSaveStatus = async () => {
    if (!can('customers.logins.block')) return;
    if (!selectedCustomer) return;
    if (!statusReason.trim()) {
      alert('Please enter a reason for this action.');
      return;
    }
    setStatusSubmitting(true);
    try {
      const cId = selectedCustomer.customerId || selectedCustomer._id;
      const res = await axios.put(`${API_BASE_URL}/customerProfiles/${cId}/toggleStatus`, {
        status: targetStatus,
        reason: statusReason.trim()
      });
      if (res.status === 200) {
        setSuccessMsg(`Customer status updated to ${targetStatus} successfully!`);
        setStatusModalOpen(false);
        fetchCustomers();
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert(err.response?.data?.message || 'Failed to update customer status.');
    } finally {
      setStatusSubmitting(false);
    }
  };

  // CSV Export
  const exportCSV = () => {
    if (!can('customers.logins.export')) return;
    const headers = [
      'SL No',
      'Customer ID',
      'Customer Name',
      'Mobile Number',
      'Email',
      'VPAs',
      'District',
      'Village/Constituency',
      'Pincode',
      'Status'
    ];

    const rows = filteredCustomers.map((c, index) => {
      const vpaList = Array.isArray(c.customerVPA) ? c.customerVPA.map(v => v.vpa || v).join('; ') : 'N/A';
      const formattedId = c.customerNo || '';
      const displayDist = (c.customerDistrictName && c.customerDistrictName !== 'N/A') ? c.customerDistrictName : 'Thiruvananthapuram';

      return [
        index + 1,
        `"${formattedId}"`,
        `"${c.customerName || ''}"`,
        `"${c.customerMobile || ''}"`,
        `"${c.customerEmail || 'N/A'}"`,
        `"${vpaList}"`,
        `"${displayDist}"`,
        `"${c.customerTalukName || 'N/A'}"`,
        `"${c.customerPincode || ''}"`,
        `"${c.customerStatus || 'active'}"`
      ];
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Customer_Logins_Manage_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Customers Logins Manage (Super Admin)
      </Typography>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Filters & Export */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Name / Mobile / ID / VPA"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="State"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <MenuItem value="All">All States</MenuItem>
              <MenuItem value="Kerala">Kerala</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="District"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
            >
              <MenuItem value="All">All Districts</MenuItem>
              {districtList.map((d) => {
                const dName = d.districtName || d.name;
                return (
                  <MenuItem key={d._id || dName} value={dName}>
                    {dName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              select
              size="small"
              label="Village / Constituency"
              value={selectedTaluk}
              onChange={(e) => setSelectedTaluk(e.target.value)}
              disabled={selectedDistrict === 'All'}
            >
              <MenuItem value="All">All Villages / Taluks</MenuItem>
              {talukList.map((t) => {
                const tName = t.talukName || t.name;
                return (
                  <MenuItem key={t._id || tName} value={tName}>
                    {tName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Pincode"
              value={pincodeFilter}
              onChange={(e) => setPincodeFilter(e.target.value)}
            />
          </Grid>
          {can('customers.logins.export') && (
            <Grid item xs={12} sm={6} md={1}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={exportCSV}
                sx={{ height: 40 }}
              >
                CSV
              </Button>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ backgroundColor: 'primary.main' }}>
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>SL No</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Mobile Number</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>VPAs</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>District / Village</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Pincode</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No customers found matching criteria.
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((c, idx) => {
                  const isBlocked = c.customerStatus === 'blocked';
                  const isInactive = c.customerStatus === 'deActive';
                  const vpaList = Array.isArray(c.customerVPA) && c.customerVPA.length > 0
                    ? c.customerVPA.map(v => v.vpa || v).join(', ')
                    : 'N/A';
                  const formattedId = customerNoOf(c);
                  const displayDist = (c.customerDistrictName && c.customerDistrictName !== 'N/A') ? c.customerDistrictName : 'Thiruvananthapuram';

                  return (
                    <TableRow key={c._id || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{formattedId}</TableCell>
                      <TableCell>{c.customerName || 'N/A'}</TableCell>
                      <TableCell>{c.customerMobile || 'N/A'}</TableCell>
                      <TableCell>{c.customerEmail || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip label={vpaList} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell>
                        {displayDist} / {c.customerTalukName || 'N/A'}
                      </TableCell>
                      <TableCell>{c.customerPincode || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={(c.customerStatus || 'active').toUpperCase()}
                          color={isBlocked ? 'error' : isInactive ? 'warning' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {can('customers.logins.edit') && (
                          <Tooltip title="Edit Customer Details">
                            <IconButton color="primary" onClick={() => handleOpenEdit(c)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canManageVpa && (
                          <Tooltip title="Add / Delete VPA">
                            <IconButton color="secondary" onClick={() => setVpaCustomer(c)}>
                              <AccountBalanceWalletIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {can('customers.logins.block') && (isBlocked ? (
                          <Tooltip title="Unblock Customer">
                            <IconButton color="success" onClick={() => handleOpenStatusModal(c, 'active')}>
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Block Customer">
                            <IconButton color="error" onClick={() => handleOpenStatusModal(c, 'blocked')}>
                              <BlockIcon />
                            </IconButton>
                          </Tooltip>
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Add / Delete VPA dialog */}
      <CustomerVpaManagerDialog
        open={Boolean(vpaCustomer)}
        onClose={() => setVpaCustomer(null)}
        customer={vpaCustomer}
        permScopes={['customers.logins', 'customers.all']}
        onUpdated={(list) => {
          setCustomers((prev) => prev.map((x) => (x._id === vpaCustomer._id ? { ...x, customerVPA: list } : x)));
          setVpaCustomer((prev) => (prev ? { ...prev, customerVPA: list } : prev));
        }}
      />

      {/* Edit Customer Dialog */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Edit Customer Details
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Customer Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Customer Email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Mobile Number"
              value={editMobile}
              onChange={(e) => setEditMobile(e.target.value)}
              fullWidth
            />
            <TextField
              label="Pincode"
              value={editPincode}
              onChange={(e) => setEditPincode(e.target.value)}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editSubmitting}>
            {editSubmitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status / Block Dialog */}
      <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: targetStatus === 'blocked' ? 'error.main' : 'success.main' }}>
          {targetStatus === 'blocked' ? 'Block Customer' : 'Unblock / Activate Customer'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2">
              Are you sure you want to change the status of <strong>{selectedCustomer?.customerName}</strong> to{' '}
              <strong>{targetStatus.toUpperCase()}</strong>?
            </Typography>
            <TextField
              label="Reason / Remarks (Required)"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              multiline
              rows={3}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={targetStatus === 'blocked' ? 'error' : 'success'}
            onClick={handleSaveStatus}
            disabled={statusSubmitting}
          >
            {statusSubmitting ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageCustomerLogins;
